import { jsx } from '@emotion/react/jsx-runtime';
import { ThemeProvider as ThemeProvider$1 } from '@emotion/react';
import { createTheme } from '@nettbureau/theme';
import { isString } from 'radash';
import { createContext, useContext, useMemo } from 'react';
import { createComponent } from '../system/factories.js';
import '@react-aria/utils';
import 'ramda';
import 'react-aria';
import { HighContrastContext } from './high-contrast-context.js';

/**
 * Context providing information about the current theme configuration to its children.
 */ const ThemeConfigContext = /*#__PURE__*/ createContext({
    colorAliases: {},
    colorInverted: false,
    colorMode: 'light'
});
/**
 * Provider for {@link ThemeConfigContext}.
 */ const ThemeConfigProvider = ThemeConfigContext.Provider;
/**
 * Provider providing {@link ThemeConfigProvider} and the configured theme using Emotion's theme
 * provider.
 *
 * @param props - Props for creating the provider component.
 */ const ThemeProvider = createComponent((props)=>{
    const currentConfig = useThemeConfig();
    const { children, ...config } = {
        ...currentConfig,
        ...props
    };
    const theme = useMemo(()=>createTheme(config), [
        config.colorAliases,
        config.colorMode,
        config.colorInverted
    ]);
    return /*#__PURE__*/ jsx(ThemeConfigProvider, {
        value: config,
        children: /*#__PURE__*/ jsx(ThemeProvider$1, {
            theme: theme,
            children: children
        })
    });
});
/**
 * Hook to access the {@link ThemeConfigContext} value.
 */ const useThemeConfig = ()=>useContext(ThemeConfigContext);
/**
 * Hook to access the current color mode.
 */ function useColorMode() {
    return useThemeConfig().colorMode ?? 'light';
}
/**
 * Hook to access the current color inversion flag.
 */ function useColorInverted() {
    return useThemeConfig().colorInverted ?? false;
}
/**
 * Hook that determines whether the color should be inverted based on the provided fill.
 *
 * @param fill - The fill color to check.
 * @param fillTone - The fill color tone to check.
 */ function useShouldInvertColors(fill, fillTone) {
    const highContrast = useContext(HighContrastContext);
    const colorInverted = useColorInverted();
    if (fill === 'base' && (!colorInverted || highContrast)) {
        return false;
    }
    if (isString(fillTone) && [
        'none',
        'soft'
    ].includes(fillTone)) {
        return colorInverted;
    }
    return isString(fillTone) ? [
        'medium',
        'bold'
    ].includes(fillTone) : fillTone >= 400;
}

export { ThemeConfigContext, ThemeConfigProvider, ThemeProvider, useColorInverted, useColorMode, useShouldInvertColors, useThemeConfig };
//# sourceMappingURL=theme-provider.js.map
